import React, { useMemo } from "react";
import { Stack, Link, Typography, FormControlLabel, Switch } from "@mui/material";
import ConfigHeader from "../ConfigHeader/config-header";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import FormSection from "../../Forms/FormSection/form-section";
import ConfigFormIcon from "../ConfigFormIcon";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import {
  ProductField,
  UpdateItemProductRootOnBasket,
  UpdateItemSettingsOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { OrderProfileSettingsBase } from "../../../Core/Api/Types/order-profile-settings-base";
import { TranskodeOptionCheckReport } from "../../../Core/Api/Types/transkode-option-check-report";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import { UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import { Licences } from "../../../Core/Api/Types/licences";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { TranskodeWatermarkVendor } from "../../../Core/Api/Types/transkode-watermark-vendor";
import ConfigCommonSectionFormNexguardWatermark from "../ConfigCommon/config-common-section-form-nexguard-watermark";
import { useTranslation } from "react-i18next";
interface TranskodeComponentsProfile extends OrderProfileSettingsBase {
  doable: boolean;
  summarizedInfos: string;
  bestEffortsWarnings: TranskodeOptionCheckReport[];
  checks: TranskodeOptionCheckReport[];
  packageType: ComponentPackageType;
  tempTerritoriesCodes: string[];
  tempAudioLanguagesCodes: string[];
  tempSubtitleLanguagesCodes: string[];
}

enum ComponentPackageType {
  Amazon = "Amazon",
  Google = "Google",
  Sony = "Sony",
  Microsoft = "Microsoft",
  Carrefour = "Carrefour",
  WuakiTv = "WuakiTv",
  Pantaflix = "Pantaflix",
  Maxdome = "Maxdome",
}

interface ConfigLegacyVODPackageProps extends ConfigBaseProps {}

const ConfigLegacyVODPackage: React.FC<ConfigLegacyVODPackageProps> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit, handleCancel: onCancel, handleChangeProduct: onChangeProduct, item } = props;

  const formId = useMemo(() => `form-legacy-components-${item.id}`, []);

  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));

  const dispatch = useAppDispatch();

  const VideoSectionId = useMemo(() => `${formId}-configure-video-section`, []);
  const AudioSectionId = useMemo(() => `${formId}-configure-audio-section`, []);
  const SubSectionId = useMemo(() => `${formId}-configure-sub-section`, []);

  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const settings: TranskodeComponentsProfile = item.selectedProduct?.settings;

  // Update settings
  const updateSettings = (settings: TranskodeComponentsProfile) => {
    dispatch(UpdateItemSettingsOnBasket({ itemId: item.id, settings: settings }));
  };

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update settings
  const handleValueInput = (value: string[]): string[] => {
    return value.reduce((acc: string[], str) => {
      if (str.includes("|")) {
        return acc.concat(str.split("|"));
      } else return acc.concat(str);
    }, []);
  };

  return (
    <Stack width={"100%"} {...props} display={formExists ? "" : "none"}>
      <ConfigHeader
        configType={OrderProductType.TranscodeComponentsLegacy}
        title={t("component:configLegacyComponents.body.configHeaderTitle.legacyVodPackageComponents")}
        handleCancel={onCancel}
        handleChangeProduct={onChangeProduct}
        itemId={-1}
      />

      <Stack spacing={1.5} width={"100%"} {...props}>
        <TerritorieCodesFormSection
          context="Video"
          formId={formId}
          sectionId={VideoSectionId}
          val={settings.tempTerritoriesCodes}
          onUpdate={(value) => {
            const newSettings = { ...settings };
            newSettings.tempTerritoriesCodes = handleValueInput(value);
            updateSettings(newSettings);
          }}
          onUnselectAll={() => {
            const newSettings = { ...settings };
            newSettings.tempTerritoriesCodes = [];
            updateSettings(newSettings);
          }}
        />

        <TerritorieCodesFormSection
          context="Audio"
          formId={formId}
          sectionId={AudioSectionId}
          val={settings.tempAudioLanguagesCodes}
          onUpdate={(value) => {
            const newSettings = { ...settings };
            newSettings.tempAudioLanguagesCodes = handleValueInput(value);
            updateSettings(newSettings);
          }}
          onUnselectAll={() => {
            const newSettings = { ...settings };
            newSettings.tempAudioLanguagesCodes = [];
            updateSettings(newSettings);
          }}
        />

        <TerritorieCodesFormSection
          context="Subtitle"
          formId={formId}
          sectionId={SubSectionId}
          val={settings.tempSubtitleLanguagesCodes}
          onUpdate={(value) => {
            const newSettings = { ...settings };
            newSettings.tempSubtitleLanguagesCodes = handleValueInput(value);
            updateSettings(newSettings);
          }}
          onUnselectAll={() => {
            const newSettings = { ...settings };
            newSettings.tempSubtitleLanguagesCodes = [];
            updateSettings(newSettings);
          }}
        />

        <ConfigCommonSectionFormNexguardWatermark item={item} />
      </Stack>

      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValid} />
    </Stack>
  );
};

export default ConfigLegacyVODPackage;

interface TerritorieCodesFormSectionProps {
  context: "Video" | "Audio" | "Subtitle";
  formId: string;
  sectionId: string;
  val: any;
  onUnselectAll: () => void;
  onUpdate: (value: string[]) => void;
}

export const TerritorieCodesFormSection: React.FC<TerritorieCodesFormSectionProps> = (props) => {
  const { t } = useTranslation();
  const { context, formId, sectionId, val, onUnselectAll, onUpdate } = props;
  const isConfigureSectionValid = useAppSelector(GetSectionIsValid(formId, sectionId));

  return (
    <FormSection
      icon={<ConfigFormIcon icon={context === "Video" ? "video" : context === "Audio" ? "audio" : "subtitles"} />}
      title={t("form:section.title.territoriesCodes", { context })}
      status={isConfigureSectionValid ? "complete" : "warning"}
      statusText={isConfigureSectionValid ? t("common:configured") : t("common:configurationRequired")}
    >
      <Stack spacing={2} width={"100%"} {...props}>
        <FormAutocomplete
          label={t("field:label.addManyTerritoriesCodes")}
          validationProps={{
            formId: formId,
            formSectionId: sectionId,
            fieldId: context + "-codes",
            required: true,
          }}
          multiple
          freeSolo
          onChange={(value) => onUpdate(value)}
          options={[]}
          value={val}
        />
        <Stack mt={2} direction="row" justifyContent="flex-end" spacing={1}>
          <Link underline="hover" variant="body2" color="primary.text" onClick={onUnselectAll}>
            {t("common:unselectAllUpperCase")}
          </Link>
        </Stack>
      </Stack>
    </FormSection>
  );
};
